// client/src/components/bracket/ScoreReportingModal.js
import React, { useState, useEffect, useMemo, useRef } from 'react'
import './ScoreReportingModal.css'
import {
  validateMatchSets,
  isValidSetScore,
  getScoreErrorMessage,
} from '../../utils/tennisScoreValidation'
import { isRetirementStatus } from '../../utils/matchStatus'
import {
  MATCH_FORMAT,
  normalizeMatchFormat,
} from '../../constants/matchFormats'
import { getMatchFormatRules } from '../../utils/matchFormatRules'

/**
 * ScoreReportingModal - Modal for reporting playoff match scores
 *
 * Props:
 * - isOpen: boolean to control modal visibility
 * - onClose: function called when modal should close
 * - match: the match object to report scores for
 * - onSubmit: async function(matchId, setScores, options) called on submit
 * - isAdmin: boolean indicating if user is an admin
 * - currentPlayerId: the current user's player ID
 */
export default function ScoreReportingModal({
  isOpen,
  onClose,
  match,
  onSubmit,
  isAdmin = false,
  currentPlayerId,
  matchFormat,
}) {
  const [sets, setSets] = useState([
    { home_games: 0, away_games: 0 },
    { home_games: 0, away_games: 0 },
    { home_games: 0, away_games: 0 },
  ])
  const [outcome, setOutcome] = useState('normal')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const [setErrors, setSetErrors] = useState({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const effectiveMatchFormat = normalizeMatchFormat(
    matchFormat ?? match?.match_format,
  )
  const isProSet = effectiveMatchFormat === MATCH_FORMAT.PRO_SET_8
  const { maxGamesPerSet } = getMatchFormatRules(effectiveMatchFormat)

  // Ref for focus management
  const modalContentRef = useRef(null)

  // Reset state when modal opens with a new match
  useEffect(() => {
    if (isOpen && match) {
      setSets(
        isProSet
          ? [{ home_games: 0, away_games: 0 }]
          : [
              { home_games: 0, away_games: 0 },
              { home_games: 0, away_games: 0 },
              { home_games: 0, away_games: 0 },
            ],
      )
      setOutcome('normal')
      setShowValidationErrors(false)
      setValidationErrors([])
      setSetErrors({})
    }
  }, [isOpen, match, isProSet])

  // Focus management for accessibility - focus modal when it opens
  useEffect(() => {
    if (isOpen && modalContentRef.current) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        const closeButton =
          modalContentRef.current?.querySelector('.score-modal-close')
        if (closeButton) {
          closeButton.focus()
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  // Accessibility: Close modal on Escape key press
  useEffect(() => {
    if (!isOpen) return
    function handleKeyDown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault()
        onClose && onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  // Extract player names from match
  const homePlayerName = useMemo(() => {
    if (!match) return 'Home Player'
    if (match.home_first_name && match.home_last_name) {
      return `${match.home_first_name} ${match.home_last_name}`.trim()
    }
    return match.home_player_id ? `Player ${match.home_player_id}` : 'TBD'
  }, [match])

  const awayPlayerName = useMemo(() => {
    if (!match) return 'Away Player'
    if (match.away_first_name && match.away_last_name) {
      return `${match.away_first_name} ${match.away_last_name}`.trim()
    }
    return match.away_player_id ? `Player ${match.away_player_id}` : 'TBD'
  }, [match])

  // Extract seeds from match columns
  const seeds = useMemo(() => {
    if (!match) return { home: null, away: null }
    return {
      home: match.home_seed ?? null,
      away: match.away_seed ?? null,
    }
  }, [match])

  // Validate sets whenever they change
  useEffect(() => {
    const isRetired = isRetirementStatus(outcome)
    const validation = validateMatchSets(sets, isRetired, effectiveMatchFormat)
    setValidationErrors(validation.errors)

    const newSetErrors = {}
    sets.forEach((set, index) => {
      if (set.home_games > 0 || set.away_games > 0) {
        if (
          !isValidSetScore(set.home_games, set.away_games, effectiveMatchFormat)
        ) {
          newSetErrors[index] = getScoreErrorMessage(
            set.home_games,
            set.away_games,
            effectiveMatchFormat,
          )
        }
      }
    })
    setSetErrors(newSetErrors)
  }, [sets, outcome, effectiveMatchFormat])

  const hasAnyScores = sets.some((s) => s.home_games > 0 || s.away_games > 0)
  const isRetirement = isRetirementStatus(outcome)
  const canSubmit =
    (hasAnyScores && validationErrors.length === 0) || isRetirement

  // Check if admin is reporting for someone else's match
  const isReportingForOthers = useMemo(() => {
    if (!isAdmin || !match || !currentPlayerId) return false
    return (
      match.home_player_id !== currentPlayerId &&
      match.away_player_id !== currentPlayerId
    )
  }, [isAdmin, match, currentPlayerId])

  // Adjust a single set score
  const adjust = (setIndex, side, delta) => {
    setSets((prev) =>
      prev.map((s, idx) => {
        if (idx !== setIndex) return s
        const key = side === 'home' ? 'home_games' : 'away_games'
        const raw = s[key] + delta
        const clamped = Math.max(0, Math.min(maxGamesPerSet, raw))
        return { ...s, [key]: clamped }
      }),
    )
  }

  const handleSubmit = async () => {
    setShowValidationErrors(true)
    const isRetired = isRetirementStatus(outcome)
    const validation = validateMatchSets(sets, isRetired, effectiveMatchFormat)

    if (!validation.success && !isRetired) {
      return
    }

    setIsSubmitting(true)
    try {
      const scoresToSubmit = sets.filter(
        (s) => s.home_games > 0 || s.away_games > 0,
      )

      const options = {}
      if (outcome === 'home_retired' || outcome === 'away_retired') {
        options.walkover = true
        options.winnerPlayerId =
          outcome === 'home_retired'
            ? match.away_player_id
            : match.home_player_id
      }

      await onSubmit(match.id, scoresToSubmit, options)
      onClose()
    } catch (error) {
      console.error('Failed to submit score:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen || !match) return null

  return (
    <div className="score-modal-overlay" onClick={onClose}>
      <div
        className="score-modal-content"
        onClick={(e) => e.stopPropagation()}
        ref={modalContentRef}
        data-testid="score-report-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="score-modal-title"
      >
        <div className="score-modal-header">
          <h3 id="score-modal-title">
            <span className="me-2" aria-hidden="true">
              🎾
            </span>
            Report Match Score
          </h3>
          <button
            className="score-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="score-modal-body">
          {/* Match Info */}
          <div className="match-info-section mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <div className="player-info">
                <span className="badge bg-light text-dark me-2">
                  {seeds.home || '?'}
                </span>
                <span className="fw-semibold">{homePlayerName}</span>
              </div>
              <span className="text-muted">vs</span>
              <div className="player-info">
                <span className="badge bg-light text-dark me-2">
                  {seeds.away || '?'}
                </span>
                <span className="fw-semibold">{awayPlayerName}</span>
              </div>
            </div>
            <div className="text-center mt-2">
              <small className="text-muted">
                Round {match.round} • Match {match.position || 'N/A'}
                {isProSet && ' • Pro Set 8'}
              </small>
            </div>
          </div>

          {/* Admin Warning */}
          {isReportingForOthers && (
            <div
              className="alert alert-warning d-flex align-items-center mb-3"
              role="alert"
            >
              <span className="me-2" aria-hidden="true">
                ⚠️
              </span>
              <div>
                <strong>Admin Override:</strong> You are reporting a score for a
                match you are not participating in.
              </div>
            </div>
          )}

          {/* Score Entry Grid */}
          <div className="score-entry-section">
            <div className="score-grid-header">
              <div></div>
              <div className="text-center fw-semibold">
                {homePlayerName.split(' ')[0]}
              </div>
              <div className="text-center fw-semibold">
                {awayPlayerName.split(' ')[0]}
              </div>
            </div>

            {sets.map((set, i) => (
              <div key={i} className="score-grid-row">
                <div className="set-label">
                  {isProSet ? 'Pro Set' : `Set ${i + 1}`}
                </div>
                <div className="score-controls">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => adjust(i, 'home', -1)}
                    disabled={isSubmitting}
                    data-testid={`score-input-home-set-${i + 1}-minus`}
                    aria-label={`Decrease home games for set ${i + 1}`}
                  >
                    −
                  </button>
                  <span
                    className={`score-value ${
                      showValidationErrors && setErrors[i] ? 'invalid' : ''
                    }`}
                  >
                    {set.home_games}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => adjust(i, 'home', 1)}
                    disabled={isSubmitting}
                    data-testid={`score-input-home-set-${i + 1}-plus`}
                    aria-label={`Increase home games for set ${i + 1}`}
                  >
                    +
                  </button>
                </div>
                <div className="score-controls">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => adjust(i, 'away', -1)}
                    disabled={isSubmitting}
                    data-testid={`score-input-away-set-${i + 1}-minus`}
                    aria-label={`Decrease away games for set ${i + 1}`}
                  >
                    −
                  </button>
                  <span
                    className={`score-value ${
                      showValidationErrors && setErrors[i] ? 'invalid' : ''
                    }`}
                  >
                    {set.away_games}
                  </span>
                  <button
                    className="btn btn-sm btn-outline-success"
                    onClick={() => adjust(i, 'away', 1)}
                    disabled={isSubmitting}
                    data-testid={`score-input-away-set-${i + 1}-plus`}
                    aria-label={`Increase away games for set ${i + 1}`}
                  >
                    +
                  </button>
                </div>
                {showValidationErrors && setErrors[i] && (
                  <div className="set-error">{setErrors[i]}</div>
                )}
              </div>
            ))}
          </div>

          {/* Validation Errors */}
          {showValidationErrors &&
            validationErrors.length > 0 &&
            !isRetirement && (
              <div className="alert alert-warning mt-3 mb-0">
                <ul className="mb-0 ps-3">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

          {/* Match Outcome Options */}
          <div className="outcome-section mt-4">
            <h6 className="text-muted mb-3">Match Outcome</h6>
            <div className="form-check">
              <input
                id="outcome-normal"
                name="outcome"
                type="radio"
                className="form-check-input"
                value="normal"
                checked={outcome === 'normal'}
                onChange={() => setOutcome('normal')}
                disabled={isSubmitting}
              />
              <label className="form-check-label" htmlFor="outcome-normal">
                Match completed normally
              </label>
            </div>
            {isAdmin && (
              <>
                <div className="form-check">
                  <input
                    id="outcome-home-retired"
                    name="outcome"
                    type="radio"
                    className="form-check-input"
                    value="home_retired"
                    checked={outcome === 'home_retired'}
                    onChange={() => setOutcome('home_retired')}
                    disabled={isSubmitting}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="outcome-home-retired"
                  >
                    {homePlayerName} retired/forfeited ({awayPlayerName} wins)
                  </label>
                </div>
                <div className="form-check">
                  <input
                    id="outcome-away-retired"
                    name="outcome"
                    type="radio"
                    className="form-check-input"
                    value="away_retired"
                    checked={outcome === 'away_retired'}
                    onChange={() => setOutcome('away_retired')}
                    disabled={isSubmitting}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="outcome-away-retired"
                  >
                    {awayPlayerName} retired/forfeited ({homePlayerName} wins)
                  </label>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="score-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="btn btn-success"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            data-testid="submit-score-button"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Score'}
          </button>
        </div>
      </div>
    </div>
  )
}
